import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { getDatabase, ref, set, get, push, update, child } from 'firebase/database';

const firebaseConfig = {
  apiKey: 'AIzaSyAeI8YUdpDYPaXjM42_g8ryMK-D621FUAo',
  authDomain: 'vibecheck-fd099.firebaseapp.com',
  databaseURL: 'https://vibecheck-fd099-default-rtdb.asia-southeast1.firebasedatabase.app',
  projectId: 'vibecheck-fd099',
  storageBucket: 'vibecheck-fd099.firebasestorage.app',
  messagingSenderId: '344072719369',
  appId: '1:344072719369:web:1ecdf740179173c7b17cd8',
  measurementId: 'G-PFVYVMRMGZ',
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
export const auth = getAuth(app);
export const database = getDatabase(app);

export const getData = async (path) => {
  console.log('Fetching data...');
  eventLogger('location_fetched', { path: path });
  const dbRef = ref(database);
  return await get(child(dbRef, path.replaceAll('.', '')))
    .then((snapshot) => snapshot.val())
    .catch((error) => console.error(error));
};

export const writeData = async (path, fields, error_message = 'Failed to save data: ') => {
  await set(ref(database, path.replaceAll('.', '')), fields).catch((error) => console.error(error_message, error));
};

// More correct to use when just updating fields instead of setting new ones
export const updateData = async (path, fields, error_message = 'Failed to update data: ') => {
  await update(ref(database, path.replaceAll('.', '')), fields).catch((error) => console.error(error_message, error));
};

export const pushData = async (path, fields, error_message = 'Failed to create new entry: ') => {
  await push(ref(database, path.replaceAll('.', '')), fields).catch((error) => console.error(error_message, error));
};

export const eventLogger = async (event_name, params) => {
  logEvent(analytics, event_name, params);
};

// USER MANAGEMENT FUNCTIONS
export const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    if (displayName) {
      await updateProfile(user, { displayName });
    }

    await eventLogger('sign_up', { method: 'email_password' });

    await writeData(`users/${user.uid}`, {
      email: user.email,
      displayName: displayName || null,
      createdAt: new Date().toISOString(),
    });

    return { success: true, user };
  } catch (error: any) {
    console.error('Sign up error:', error);
    return { success: false, error: error.message };
  }
};

export const signInWithEmail = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Log login event
    await eventLogger('login', { method: 'email_password' });

    return { success: true, user };
  } catch (error: any) {
    console.error('Sign in error:', error);
    return { success: false, error: error.message };
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
    await eventLogger('logout', {});
    return { success: true };
  } catch (error: any) {
    console.error('Sign out error:', error);
    return { success: false, error: error.message };
  }
};

export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    await eventLogger('password_reset_requested', { method: 'email_password', email });
    return { success: true };
  } catch (error: any) {
    console.error('Password reset error:', error);
    return { success: false, error: error.message };
  }
};

export const changePassword = async (currentPassword: string, newPassword: string) => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      return { success: false, error: 'No user logged in' };
    }

    // Re-authenticate user before changing password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);
    await eventLogger('password_changed', {});

    return { success: true };
  } catch (error: any) {
    console.error('Change password error:', error);
    return { success: false, error: error.message };
  }
};

export const updateUserProfile = async (updates: { displayName?: string }) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    await updateProfile(user, updates);

    // Update in database as well
    await updateData(`users/${user.uid}`, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });

    await eventLogger('profile_updated', {});

    return { success: true };
  } catch (error: any) {
    console.error('Update profile error:', error);
    return { success: false, error: error.message };
  }
};

// Auth state listener
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!auth.currentUser;
};
